import type { ConfigRevision } from "../../../configTypes";

export interface SaveEvent {
  /** First revision id in the cluster (deterministic key for React lists). */
  id: string;
  /** Timestamp taken from the first revision in the cluster. */
  createdAt: string;
  /** Actor email taken from the first revision; null if system-driven. */
  createdByEmail: string | null;
  /** All revisions whose createdAt is within `windowMs` of the cluster anchor. */
  revisions: ConfigRevision[];
}

/**
 * Group consecutive revisions whose `createdAt` falls within `windowMs`. One user save creates
 * several ConfigRevision rows — one per changed field, typically `vars` plus `secrets` — written
 * with near-identical timestamps, so clustering surfaces them as one save event. Input must be
 * sorted by `createdAt` DESCENDING; each cluster's anchor timestamp and actor come from row one.
 */
export function clusterRevisions(
  revisions: ConfigRevision[],
  windowMs: number = 2000,
): SaveEvent[] {
  if (revisions.length === 0) return [];

  const events: SaveEvent[] = [];
  let current: SaveEvent | null = null;
  let anchorMs = 0;

  for (const revision of revisions) {
    const ms = new Date(revision.createdAt).getTime();
    if (current === null || Math.abs(anchorMs - ms) > windowMs) {
      current = {
        id: revision.id,
        createdAt: revision.createdAt,
        createdByEmail: revision.createdByEmail,
        revisions: [revision],
      };
      anchorMs = ms;
      events.push(current);
    } else {
      current.revisions.push(revision);
    }
  }
  return events;
}
